import { z } from 'zod'
import { TextFormatSchema } from './primitives'
import { ZoneSchema } from './template'

export const BlockTierSchema = z.enum(['platform', 'custom'])
export type BlockTier = z.infer<typeof BlockTierSchema>

/**
 * @deprecated Removed from `BlockDefinition` on 2026-05-24. Retained only for
 * the legacy-to-current migration helper (see Task 4). New code uses
 * {@link VariableMetaSchema} on `BlockDefinition.variables`.
 */
export const BlockInputTypeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('field'),
    fieldKey: z.string(),
    label: z.string().optional(),
    required: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('text'),
    default: z.string().optional(),
    label: z.string().optional(),
    required: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('number'),
    default: z.number().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    label: z.string().optional(),
    required: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('format'),
    default: z.string().optional(),
    label: z.string().optional(),
    required: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('enum'),
    options: z.array(z.string()).min(1),
    default: z.string().optional(),
    label: z.string().optional(),
    required: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('boolean'),
    default: z.boolean().optional(),
    label: z.string().optional(),
    required: z.boolean().optional(),
  }),
])
/** @deprecated See {@link BlockInputTypeSchema}. */
export type BlockInputType = z.infer<typeof BlockInputTypeSchema>

export const VariableTypeSchema = z.enum([
  'text',
  'number',
  'date',
  'boolean',
  'enum',
])
export type VariableType = z.infer<typeof VariableTypeSchema>

export const VariableMetaSchema = z.object({
  type: VariableTypeSchema,
  default: z.string().optional(),
  format: TextFormatSchema.optional(),
  enumOptions: z.array(z.string()).optional(),
})
export type VariableMeta = z.infer<typeof VariableMetaSchema>

export const BlockDefinitionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  version: z.number().int().positive(),
  tier: BlockTierSchema,
  widthMm: z.number().positive().max(300),
  heightMm: z.number().positive().max(300),
  /**
   * Designer-only UI state: when true, the editors disable label
   * width/height editing (typed ruler inputs + resize drag handles).
   * The ZPL pipeline ignores this field — no print-output impact.
   */
  dimensionsLocked: z.boolean().optional(),
  icon: z.string().optional(),
  category: z.string().optional(),
  variables: z.record(z.string(), VariableMetaSchema).optional(),
  layout: ZoneSchema,
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
  createdBy: z.string().optional(),
})
export type BlockDefinition = z.infer<typeof BlockDefinitionSchema>

/**
 * Public metadata shape for a block, derived from its `BlockDefinition`
 * record plus host-computed `latestVersion` / `hasDraft` fields. Adapters
 * MUST compute and supply these two fields when constructing
 * `BlockDefinitionMeta` instances; they are not stored alongside the
 * block body. `latestVersion: 0` indicates a block with no published
 * version yet (draft-only). `hasDraft: true` indicates a draft exists
 * for this block id alongside any published versions.
 */
export const BlockDefinitionMetaSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  version: z.number().int().min(0),
  tier: BlockTierSchema,
  icon: z.string().optional(),
  category: z.string().optional(),
  widthMm: z.number().positive().max(300),
  heightMm: z.number().positive().max(300),
  updatedAt: z.string().datetime().optional(),
  latestVersion: z.number().int().min(0),
  hasDraft: z.boolean(),
})
export type BlockDefinitionMeta = z.infer<typeof BlockDefinitionMetaSchema>

export {
  foldAlignmentIntoPlacement,
  migrateBlockDefinition,
  migrateComponentValueSources,
  withBlockDimensionDefaults,
} from './migrate-block'
